/**
 * Standard populations for direct age standardization, in the 18 age groups every one of
 * them can be reduced to: 0–4, 5–9, …, 80–84, 85 and over.
 *
 * Sources, all fetched from the publishers on 16 October 2026 and compared line by line:
 *
 * - US 2000 standard population — National Cancer Institute SEER, "Standard Populations —
 *   19 Age Groups" (https://seer.cancer.gov/stdpopulations/stdpop.19ages.html), which
 *   reproduces the Census Bureau projection used by NCHS (Klein & Schoenborn, Healthy
 *   People Statistical Notes 20, 2001). Counts for "under 1" (3,794,901) and "1–4"
 *   (15,191,619) are added to give 0–4 (18,986,520). Total 274,633,642.
 * - WHO World Standard Population, average world age structure 2000–2025 — Ahmad OB,
 *   Boschi-Pinto C, Lopez AD, Murray CJL, Lozano R, Inoue M. "Age standardization of
 *   rates: a new WHO standard." GPE Discussion Paper Series No. 31, WHO, 2001
 *   (https://cdn.who.int/media/docs/default-source/gho-documents/global-health-estimates/
 *   gpe_discussion_paper_series_paper31_2001_age_standardization_rates.pdf), Table 4, in
 *   per cent. The paper's finer 85–89 (0.44), 90–94 (0.15), 95–99 (0.04) and 100+ (0.005)
 *   groups are published as the aggregate 85+ (0.63) in its comparison table, which is
 *   what is used here. The published percentages are rounded and sum to 100.03; weights
 *   are normalised before use.
 * - European Standard Population 2013 (ESP 2013) — Eurostat, "Revision of the European
 *   Standard Population: Report of Eurostat's task force" (KS-RA-13-028-EN, 2013), Annex F,
 *   per 100,000: under 1: 1,000; 1–4: 4,000; 5–9 to 15–19: 5,500; 20–24 and 25–29: 6,000;
 *   30–34: 6,500; 35–54: 7,000; 55–59: 6,500; 60–64: 6,000; 65–69: 5,500; 70–74: 5,000;
 *   75–79: 4,000; 80–84: 2,500; 85–89: 1,500; 90–94: 800; 95+: 200. Under 1 and 1–4 give
 *   0–4 (5,000) and 85–89, 90–94 and 95+ give 85+ (2,500).
 */

export type StandardId = "us2000" | "who" | "esp2013";

export const AGE_GROUP_LABELS: readonly string[] = [
  "0–4",
  "5–9",
  "10–14",
  "15–19",
  "20–24",
  "25–29",
  "30–34",
  "35–39",
  "40–44",
  "45–49",
  "50–54",
  "55–59",
  "60–64",
  "65–69",
  "70–74",
  "75–79",
  "80–84",
  "85+",
];

export const STANDARD_POPULATIONS: Record<StandardId, readonly number[]> = {
  us2000: [
    18_986_520, 19_919_840, 20_056_779, 19_819_518, 18_257_225, 17_722_067,
    19_511_370, 22_179_956, 22_479_229, 19_805_793, 17_224_359, 13_307_234,
    10_654_272, 9_409_940, 8_725_574, 7_414_559, 4_900_234, 4_259_173,
  ],
  who: [
    8.86, 8.69, 8.6, 8.47, 8.22, 7.93, 7.61, 7.15, 6.59, 6.04, 5.37, 4.55, 3.72,
    2.96, 2.21, 1.52, 0.91, 0.63,
  ],
  esp2013: [
    5000, 5500, 5500, 5500, 6000, 6000, 6500, 7000, 7000, 7000, 7000, 6500,
    6000, 5500, 5000, 4000, 2500, 2500,
  ],
};

/** The weights of a standard: each group's share of the total. */
export function standardWeights(counts: readonly number[]): number[] {
  const total = counts.reduce((sum, value) => sum + value, 0);
  return counts.map((value) => value / total);
}
